import React from 'react';
import { Heart, HeartHandshake, Users, Focus, Smile, Flame, Gift, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { aboutContent, coreValues } from '../mock';
import { useTranslation } from '../i18n/LanguageContext';

const iconMap = {
  HeartHandshake,
  Heart,
  Users,
  Focus,
  Smile,
  Flame,
  Gift,
  Crown
};

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="text-white py-24 md:py-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/holy-fire-bg.jpg')" }}></div>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8 fade-in">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 uppercase tracking-[0.3em] text-xs">
              {t('about.badge')}
            </Badge>
            <h1 className="display-hero text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              {t('about.heroLine1')} <span className="italic font-light">{t('about.heroLine1Accent')}</span>
            </h1>
            <h2 className="display-hero text-xl sm:text-2xl md:text-3xl italic font-light text-white/90">
              {t('about.heroLine2')}
            </h2>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              {t('about.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* OUR MISSION */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12 text-center">
              <Badge variant="secondary" className="mb-6 uppercase tracking-[0.3em] text-xs">
                {t('about.missionBadge')}
              </Badge>
              <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
                <span className="italic font-light">{t('about.missionTitleAccent')}</span> {t('about.missionTitleRest')}
              </h2>
            </div>
            <div className="text-left space-y-5">
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('about.missionParagraph0')}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('about.missionParagraph1')}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('about.missionParagraph2')}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('about.missionParagraph3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scripture Foundation */}
      <section className="py-20 md:py-32 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Flame className="h-16 w-16 mx-auto text-primary-foreground drop-shadow-lg" />
            <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              {t('about.holyFireTitle')} <span className="italic font-light">{t('about.holyFireAccent')}</span>
            </h2>
            <p className="uppercase tracking-[0.3em] text-xs md:text-sm font-semibold opacity-90">
              {t('about.holyFireBadge')}
            </p>
            <blockquote className="display-hero text-2xl md:text-3xl italic font-light leading-snug max-w-3xl mx-auto">
              {`"${t(aboutContent.scripture)}"`}
            </blockquote>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 md:py-32 section-gradient">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-6 uppercase tracking-[0.3em] text-xs">
              {t('about.coreValuesBadge')}
            </Badge>
            <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              {t('about.coreValuesTitle')} <span className="italic font-light text-gradient">{t('about.coreValuesAccent')}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {coreValues.map((value) => {
              const IconComponent = iconMap[value.icon];
              return (
                <Card key={value.id} className="text-center card-hover">
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{t(value.title)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {t(value.description)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
